import { createClient } from '@supabase/supabase-js'
import { SUPABASE_URL, SUPABASE_ANON_KEY, APP_URL, isSupabaseConfigured } from '../config'

// Initialize Supabase client
let supabaseClient = null

export function getSupabase() {
  if (!isSupabaseConfigured()) {
    console.warn('Supabase is not configured. Please update config.js with your credentials.')
    return null
  }

  if (!supabaseClient) {
    supabaseClient = createClient(SUPABASE_URL, SUPABASE_ANON_KEY)
  }

  return supabaseClient
}

// Database helpers
export const db = {
  // Businesses
  async getBusinesses() {
    const client = getSupabase()
    if (!client) return { data: [], error: null }

    const { data, error } = await client
      .from('businesses')
      .select('*')
      .order('name')

    return { data: data || [], error }
  },

  async getBusiness(id) {
    const client = getSupabase()
    if (!client) return { data: null, error: null }

    const { data, error } = await client
      .from('businesses')
      .select('*')
      .eq('id', id)
      .single()

    return { data, error }
  },

  async createBusiness(business) {
    const client = getSupabase()
    if (!client) return { data: null, error: 'Supabase not configured' }

    const { data, error } = await client
      .from('businesses')
      .insert([business])
      .select()
      .single()

    return { data, error }
  },

  async updateBusiness(id, updates) {
    const client = getSupabase()
    if (!client) return { data: null, error: 'Supabase not configured' }

    const { data, error } = await client
      .from('businesses')
      .update(updates)
      .eq('id', id)
      .select()
      .single()

    return { data, error }
  },

  async deleteBusiness(id) {
    const client = getSupabase()
    if (!client) return { error: 'Supabase not configured' }

    const { error } = await client
      .from('businesses')
      .delete()
      .eq('id', id)

    return { error }
  },

  // Transactions
  async getTransactions(businessId, options = {}) {
    const client = getSupabase()
    if (!client) return { data: [], error: null }

    let query = client
      .from('transactions')
      .select('*')
      .eq('business_id', businessId)
      .order('date', { ascending: false })

    if (options.limit) {
      query = query.limit(options.limit)
    }

    const { data, error } = await query
    return { data: data || [], error }
  },

  async getAllTransactions(options = {}) {
    const client = getSupabase()
    if (!client) return { data: [], error: null }

    let query = client
      .from('transactions')
      .select('*, businesses(name, emoji)')
      .order('date', { ascending: false })

    if (options.limit) {
      query = query.limit(options.limit)
    }

    const { data, error } = await query
    return { data: data || [], error }
  },

  async createTransaction(transaction) {
    const client = getSupabase()
    if (!client) return { data: null, error: 'Supabase not configured' }

    const { data, error } = await client
      .from('transactions')
      .insert([transaction])
      .select()
      .single()

    return { data, error }
  },

  async updateTransaction(id, updates) {
    const client = getSupabase()
    if (!client) return { data: null, error: 'Supabase not configured' }

    const { data, error } = await client
      .from('transactions')
      .update(updates)
      .eq('id', id)
      .select()
      .single()

    return { data, error }
  },

  async deleteTransaction(id) {
    const client = getSupabase()
    if (!client) return { error: 'Supabase not configured' }

    const { error } = await client
      .from('transactions')
      .delete()
      .eq('id', id)

    return { error }
  },

  // Aggregate financial data
  async getBusinessFinancials(businessId) {
    const { data: transactions, error } = await this.getTransactions(businessId)

    if (error) return { data: null, error }

    const revenue = transactions
      .filter(t => t.type === 'revenue')
      .reduce((sum, t) => sum + parseFloat(t.amount), 0)

    const expenses = transactions
      .filter(t => t.type === 'expense')
      .reduce((sum, t) => sum + parseFloat(t.amount), 0)

    const profit = revenue - expenses

    return {
      data: {
        revenue,
        expenses,
        profit,
        transactionCount: transactions.length,
      },
      error: null,
    }
  },

  // All-up view across businesses
  async getAllBusinessFinancials() {
    const { data: businesses, error: bizError } = await this.getBusinesses()
    if (bizError) return { data: null, error: bizError }

    const results = await Promise.all(
      businesses.map(async (biz) => {
        const { data: financials } = await this.getBusinessFinancials(biz.id)
        return { ...biz, financials }
      })
    )

    const totals = results.reduce(
      (acc, biz) => {
        if (biz.financials) {
          acc.revenue += biz.financials.revenue
          acc.expenses += biz.financials.expenses
          acc.profit += biz.financials.profit
        }
        return acc
      },
      { revenue: 0, expenses: 0, profit: 0 }
    )

    return {
      data: {
        businesses: results,
        totals,
      },
      error: null,
    }
  },

  // Users (admin only)
  async getUsers() {
    const client = getSupabase()
    if (!client) return { data: [], error: null }

    const { data, error } = await client
      .from('profiles')
      .select('*')
      .order('name')

    return { data: data || [], error }
  },

  async updateUserRole(userId, role) {
    const client = getSupabase()
    if (!client) return { data: null, error: 'Supabase not configured' }

    const { data, error } = await client
      .from('profiles')
      .update({ role })
      .eq('id', userId)
      .select()
      .single()

    return { data, error }
  },

  async updateUser(userId, updates) {
    const client = getSupabase()
    if (!client) return { data: null, error: 'Supabase not configured' }

    const { data, error } = await client
      .from('profiles')
      .update(updates)
      .eq('id', userId)
      .select()
      .single()

    return { data, error }
  },

  async deleteUser(userId) {
    const client = getSupabase()
    if (!client) return { error: 'Supabase not configured' }

    // First, revoke all business access for this user
    const { error: accessError } = await client
      .from('business_access')
      .delete()
      .eq('user_id', userId)

    if (accessError) {
      return { error: accessError }
    }

    // Delete the user's profile (this doesn't delete from auth.users - that requires service role)
    const { error } = await client
      .from('profiles')
      .delete()
      .eq('id', userId)

    return { error }
  },

  async deactivateUser(userId) {
    const client = getSupabase()
    if (!client) return { data: null, error: 'Supabase not configured' }

    const { data, error } = await client
      .from('profiles')
      .update({ is_active: false })
      .eq('id', userId)
      .select()
      .single()

    return { data, error }
  },

  // Invitations
  async getInvitations() {
    const client = getSupabase()
    if (!client) return { data: [], error: null }

    const { data, error } = await client
      .from('invitations')
      .select('*, inviter:invited_by(name, email)')
      .order('created_at', { ascending: false })

    return { data: data || [], error }
  },

  async createInvitation(invitation) {
    const client = getSupabase()
    if (!client) return { data: null, error: 'Supabase not configured' }

    const { data, error } = await client
      .from('invitations')
      .insert([invitation])
      .select()
      .single()

    return { data, error }
  },

  async revokeInvitation(id) {
    const client = getSupabase()
    if (!client) return { error: 'Supabase not configured' }

    const { error } = await client
      .from('invitations')
      .update({ status: 'revoked' })
      .eq('id', id)

    return { error }
  },

  async inviteUserByEmail(email, role, name, invitedBy) {
    const client = getSupabase()
    if (!client) return { data: null, error: 'Supabase not configured' }

    // Create invitation record to track it
    const { data: invitation, error: inviteError } = await this.createInvitation({
      email,
      name,
      role,
      invited_by: invitedBy,
    })

    if (inviteError) {
      return { data: null, error: inviteError.message || inviteError }
    }

    // Use Supabase's built-in magic link / password reset flow
    // This sends an email via Supabase's email service (no SMTP config needed)
    // Use APP_URL for production redirects (not window.location.origin which may be localhost)
    const { data, error } = await client.auth.signInWithOtp({
      email,
      options: {
        data: { name, role },
        emailRedirectTo: `${APP_URL}/login`,
      },
    })

    if (error) {
      return { data: null, error: error.message }
    }

    return { data: { invitation }, error: null }
  },

  // Business Access
  async getBusinessAccess(businessId = null) {
    const client = getSupabase()
    if (!client) return { data: [], error: null }

    let query = client
      .from('business_access')
      .select('*, user:user_id(id, name, email), business:business_id(id, name, emoji)')
      .order('created_at', { ascending: false })

    if (businessId) {
      query = query.eq('business_id', businessId)
    }

    const { data, error } = await query
    return { data: data || [], error }
  },

  async getUserBusinessAccess(userId) {
    const client = getSupabase()
    if (!client) return { data: [], error: null }

    const { data, error } = await client
      .from('business_access')
      .select('*, business:business_id(id, name, emoji, status)')
      .eq('user_id', userId)

    return { data: data || [], error }
  },

  async grantBusinessAccess(userId, businessId, accessLevel, grantedBy) {
    const client = getSupabase()
    if (!client) return { data: null, error: 'Supabase not configured' }

    const { data, error } = await client
      .from('business_access')
      .upsert([{
        user_id: userId,
        business_id: businessId,
        access_level: accessLevel,
        granted_by: grantedBy,
      }], { onConflict: 'user_id,business_id' })
      .select()
      .single()

    return { data, error }
  },

  async revokeBusinessAccess(userId, businessId) {
    const client = getSupabase()
    if (!client) return { error: 'Supabase not configured' }

    const { error } = await client
      .from('business_access')
      .delete()
      .eq('user_id', userId)
      .eq('business_id', businessId)

    return { error }
  },

  // Subscriptions (recurring expenses)
  async getSubscriptions(businessId = null) {
    const client = getSupabase()
    if (!client) return { data: [], error: null }

    let query = client
      .from('subscriptions')
      .select('*, businesses(name, emoji)')
      .order('name')

    if (businessId) {
      query = query.eq('business_id', businessId)
    }

    const { data, error } = await query
    return { data: data || [], error }
  },

  async createSubscription(subscription) {
    const client = getSupabase()
    if (!client) return { data: null, error: 'Supabase not configured' }

    const { data, error } = await client
      .from('subscriptions')
      .insert([subscription])
      .select()
      .single()

    return { data, error }
  },

  async updateSubscription(id, updates) {
    const client = getSupabase()
    if (!client) return { data: null, error: 'Supabase not configured' }

    const { data, error } = await client
      .from('subscriptions')
      .update(updates)
      .eq('id', id)
      .select()
      .single()

    return { data, error }
  },

  async deleteSubscription(id) {
    const client = getSupabase()
    if (!client) return { error: 'Supabase not configured' }

    const { error } = await client
      .from('subscriptions')
      .delete()
      .eq('id', id)

    return { error }
  },

  // Todos
  async getTodos(businessId = null) {
    const client = getSupabase()
    if (!client) return { data: [], error: null }

    let query = client
      .from('todos')
      .select(`
        *,
        businesses(name, emoji),
        todo_subtasks(id, title, is_completed, order_position, assigned_to),
        todo_links(id, url, title)
      `)
      .order('due_date', { ascending: true })

    if (businessId) {
      query = query.eq('business_id', businessId)
    }

    const { data, error } = await query

    // Process data to add counts
    const processedData = (data || []).map(todo => ({
      ...todo,
      subtask_count: todo.todo_subtasks?.length || 0,
      subtask_completed_count: todo.todo_subtasks?.filter(s => s.is_completed).length || 0,
      link_count: todo.todo_links?.length || 0,
    }))

    return { data: processedData, error }
  },

  async createTodo(todo) {
    const client = getSupabase()
    if (!client) return { data: null, error: 'Supabase not configured' }

    const { data, error } = await client
      .from('todos')
      .insert([todo])
      .select()
      .single()

    return { data, error }
  },

  async updateTodo(id, updates) {
    const client = getSupabase()
    if (!client) return { data: null, error: 'Supabase not configured' }

    const { data, error } = await client
      .from('todos')
      .update(updates)
      .eq('id', id)
      .select()
      .single()

    return { data, error }
  },

  async deleteTodo(id) {
    const client = getSupabase()
    if (!client) return { error: 'Supabase not configured' }

    const { error } = await client
      .from('todos')
      .delete()
      .eq('id', id)

    return { error }
  },

  // ============================================
  // SUBTASKS
  // ============================================

  async getSubtasks(todoId) {
    const client = getSupabase()
    if (!client) return { data: [], error: null }

    const { data, error } = await client
      .from('todo_subtasks')
      .select('*')
      .eq('todo_id', todoId)
      .order('order_position', { ascending: true })

    return { data: data || [], error }
  },

  async createSubtask(todoId, title, assignedTo = null) {
    const client = getSupabase()
    if (!client) return { data: null, error: 'Supabase not configured' }

    // Get max order position
    const { data: existing } = await client
      .from('todo_subtasks')
      .select('order_position')
      .eq('todo_id', todoId)
      .order('order_position', { ascending: false })
      .limit(1)

    const orderPosition = existing?.length > 0 ? existing[0].order_position + 1 : 0

    const { data, error } = await client
      .from('todo_subtasks')
      .insert([{ todo_id: todoId, title, order_position: orderPosition, assigned_to: assignedTo }])
      .select()
      .single()

    return { data, error }
  },

  async updateSubtask(id, updates) {
    const client = getSupabase()
    if (!client) return { data: null, error: 'Supabase not configured' }

    const { data, error } = await client
      .from('todo_subtasks')
      .update(updates)
      .eq('id', id)
      .select()
      .single()

    return { data, error }
  },

  async toggleSubtask(id) {
    const client = getSupabase()
    if (!client) return { data: null, error: 'Supabase not configured' }

    // Get current state
    const { data: current, error: fetchError } = await client
      .from('todo_subtasks')
      .select('is_completed')
      .eq('id', id)
      .single()

    if (fetchError) return { data: null, error: fetchError }

    const { data, error } = await client
      .from('todo_subtasks')
      .update({ is_completed: !current.is_completed })
      .eq('id', id)
      .select()
      .single()

    return { data, error }
  },

  async deleteSubtask(id) {
    const client = getSupabase()
    if (!client) return { error: 'Supabase not configured' }

    const { error } = await client
      .from('todo_subtasks')
      .delete()
      .eq('id', id)

    return { error }
  },

  async reorderSubtasks(todoId, orderedIds) {
    const client = getSupabase()
    if (!client) return { error: 'Supabase not configured' }

    // Update each subtask with its new position
    const updates = orderedIds.map((id, index) =>
      client
        .from('todo_subtasks')
        .update({ order_position: index })
        .eq('id', id)
        .eq('todo_id', todoId)
    )

    const results = await Promise.all(updates)
    const error = results.find(r => r.error)?.error
    return { error: error || null }
  },

  // ============================================
  // TODO LINKS
  // ============================================

  async getTodoLinks(todoId) {
    const client = getSupabase()
    if (!client) return { data: [], error: null }

    const { data, error } = await client
      .from('todo_links')
      .select('*')
      .eq('todo_id', todoId)
      .order('created_at', { ascending: true })

    return { data: data || [], error }
  },

  async createTodoLink(todoId, url, title = null) {
    const client = getSupabase()
    if (!client) return { data: null, error: 'Supabase not configured' }

    const { data, error } = await client
      .from('todo_links')
      .insert([{ todo_id: todoId, url, title }])
      .select()
      .single()

    return { data, error }
  },

  async updateTodoLink(id, updates) {
    const client = getSupabase()
    if (!client) return { data: null, error: 'Supabase not configured' }

    const { data, error } = await client
      .from('todo_links')
      .update(updates)
      .eq('id', id)
      .select()
      .single()

    return { data, error }
  },

  async deleteTodoLink(id) {
    const client = getSupabase()
    if (!client) return { error: 'Supabase not configured' }

    const { error } = await client
      .from('todo_links')
      .delete()
      .eq('id', id)

    return { error }
  },

  // Categories
  async getCategories(businessId = null) {
    const client = getSupabase()
    if (!client) return { data: [], error: null }

    let query = client
      .from('categories')
      .select('*')
      .order('name')

    if (businessId) {
      query = query.or(`business_id.is.null,business_id.eq.${businessId}`)
    } else {
      query = query.is('business_id', null)
    }

    const { data, error } = await query
    return { data: data || [], error }
  },

  async createCategory(category) {
    const client = getSupabase()
    if (!client) return { data: null, error: 'Supabase not configured' }

    const { data, error } = await client
      .from('categories')
      .insert([category])
      .select()
      .single()

    return { data, error }
  },

  async updateCategory(id, updates) {
    const client = getSupabase()
    if (!client) return { data: null, error: 'Supabase not configured' }

    const { data, error } = await client
      .from('categories')
      .update(updates)
      .eq('id', id)
      .select()
      .single()

    return { data, error }
  },

  async deleteCategory(id) {
    const client = getSupabase()
    if (!client) return { error: 'Supabase not configured' }

    const { error } = await client
      .from('categories')
      .delete()
      .eq('id', id)

    return { error }
  },

  // Reports
  async getReports(type = null) {
    const client = getSupabase()
    if (!client) return { data: [], error: null }

    let query = client
      .from('reports')
      .select('*')
      .order('created_at', { ascending: false })

    if (type) {
      query = query.eq('type', type)
    }

    const { data, error } = await query
    return { data: data || [], error }
  },

  async createReport(report) {
    const client = getSupabase()
    if (!client) return { data: null, error: 'Supabase not configured' }

    const { data, error } = await client
      .from('reports')
      .insert([report])
      .select()
      .single()

    return { data, error }
  },

  async updateReport(id, updates) {
    const client = getSupabase()
    if (!client) return { data: null, error: 'Supabase not configured' }

    const { data, error } = await client
      .from('reports')
      .update(updates)
      .eq('id', id)
      .select()
      .single()

    return { data, error }
  },

  async deleteReport(id) {
    const client = getSupabase()
    if (!client) return { error: 'Supabase not configured' }

    const { error } = await client
      .from('reports')
      .delete()
      .eq('id', id)

    return { error }
  },

  // Audit log
  async logAction(action, tableName, recordId, oldData = null, newData = null, userId = null) {
    const client = getSupabase()
    if (!client) return { error: 'Supabase not configured' }

    const { error } = await client
      .from('audit_log')
      .insert([{
        user_id: userId,
        action,
        table_name: tableName,
        record_id: recordId,
        old_data: oldData,
        new_data: newData,
      }])

    return { error }
  },

  // ============================================
  // CLIENTS
  // ============================================

  async getClients(businessId = null) {
    const client = getSupabase()
    if (!client) return { data: [], error: null }

    let query = client
      .from('clients')
      .select('*, businesses(name, emoji)')
      .order('name')

    if (businessId) {
      query = query.eq('business_id', businessId)
    }

    const { data, error } = await query
    return { data: data || [], error }
  },

  async getClient(id) {
    const client = getSupabase()
    if (!client) return { data: null, error: null }

    const { data, error } = await client
      .from('clients')
      .select('*, businesses(name, emoji)')
      .eq('id', id)
      .single()

    return { data, error }
  },

  async createClient(clientData) {
    const client = getSupabase()
    if (!client) return { data: null, error: 'Supabase not configured' }

    const { data, error } = await client
      .from('clients')
      .insert([clientData])
      .select()
      .single()

    return { data, error }
  },

  async updateClient(id, updates) {
    const client = getSupabase()
    if (!client) return { data: null, error: 'Supabase not configured' }

    const { data, error } = await client
      .from('clients')
      .update(updates)
      .eq('id', id)
      .select()
      .single()

    return { data, error }
  },

  async deleteClient(id) {
    const client = getSupabase()
    if (!client) return { error: 'Supabase not configured' }

    const { error } = await client
      .from('clients')
      .delete()
      .eq('id', id)

    return { error }
  },

  // ============================================
  // INVOICES
  // ============================================

  async getInvoices(businessId = null, status = null) {
    const client = getSupabase()
    if (!client) return { data: [], error: null }

    let query = client
      .from('invoices')
      .select('*, clients(name, email, company), businesses(name, emoji)')
      .order('created_at', { ascending: false })

    if (businessId) {
      query = query.eq('business_id', businessId)
    }

    if (status) {
      query = query.eq('status', status)
    }

    const { data, error } = await query
    return { data: data || [], error }
  },

  async getInvoice(id) {
    const client = getSupabase()
    if (!client) return { data: null, error: null }

    const { data, error } = await client
      .from('invoices')
      .select('*, clients(name, email, company, address), businesses(name, emoji), invoice_items(*)')
      .eq('id', id)
      .single()

    return { data, error }
  },

  async createInvoice(invoiceData) {
    const client = getSupabase()
    if (!client) return { data: null, error: 'Supabase not configured' }

    // Generate invoice number
    const { data: numberData, error: numberError } = await client
      .rpc('generate_invoice_number', { p_business_id: invoiceData.business_id })

    if (numberError) {
      return { data: null, error: numberError }
    }

    const { data, error } = await client
      .from('invoices')
      .insert([{ ...invoiceData, invoice_number: numberData }])
      .select()
      .single()

    return { data, error }
  },

  async updateInvoice(id, updates) {
    const client = getSupabase()
    if (!client) return { data: null, error: 'Supabase not configured' }

    const { data, error } = await client
      .from('invoices')
      .update(updates)
      .eq('id', id)
      .select()
      .single()

    return { data, error }
  },

  async deleteInvoice(id) {
    const client = getSupabase()
    if (!client) return { error: 'Supabase not configured' }

    const { error } = await client
      .from('invoices')
      .delete()
      .eq('id', id)

    return { error }
  },

  // Invoice Items
  async addInvoiceItem(item) {
    const client = getSupabase()
    if (!client) return { data: null, error: 'Supabase not configured' }

    const { data, error } = await client
      .from('invoice_items')
      .insert([item])
      .select()
      .single()

    return { data, error }
  },

  async updateInvoiceItem(id, updates) {
    const client = getSupabase()
    if (!client) return { data: null, error: 'Supabase not configured' }

    const { data, error } = await client
      .from('invoice_items')
      .update(updates)
      .eq('id', id)
      .select()
      .single()

    return { data, error }
  },

  async deleteInvoiceItem(id) {
    const client = getSupabase()
    if (!client) return { error: 'Supabase not configured' }

    const { error } = await client
      .from('invoice_items')
      .delete()
      .eq('id', id)

    return { error }
  },

  // Mark invoice as sent
  async sendInvoice(id) {
    const client = getSupabase()
    if (!client) return { data: null, error: 'Supabase not configured' }

    const { data, error } = await client
      .from('invoices')
      .update({ status: 'sent', sent_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single()

    return { data, error }
  },

  // Mark invoice as paid
  async markInvoicePaid(id, amount = null) {
    const client = getSupabase()
    if (!client) return { data: null, error: 'Supabase not configured' }

    // Get invoice to determine amount if not provided
    const { data: invoice } = await this.getInvoice(id)

    const { data, error } = await client
      .from('invoices')
      .update({
        status: 'paid',
        paid_at: new Date().toISOString(),
        amount_paid: amount || invoice?.total || 0
      })
      .eq('id', id)
      .select()
      .single()

    return { data, error }
  },

  // ============================================
  // CENTRAL HUB (Products, Expenses, Team, Meetings, Docs)
  // ============================================

  async getProducts() {
    const client = getSupabase()
    if (!client) return { data: [], error: null }

    const { data, error } = await client
      .from('products')
      .select('*')
      .order('name')

    return { data: data || [], error }
  },

  async createProduct(product) {
    const client = getSupabase()
    if (!client) return { data: null, error: 'Supabase not configured' }

    const { data, error } = await client
      .from('products')
      .insert([product])
      .select()
      .single()

    return { data, error }
  },

  async updateProduct(id, updates) {
    const client = getSupabase()
    if (!client) return { data: null, error: 'Supabase not configured' }

    const { data, error } = await client
      .from('products')
      .update(updates)
      .eq('id', id)
      .select()
      .single()

    return { data, error }
  },

  async getExpenses(options = {}) {
    const client = getSupabase()
    if (!client) return { data: [], error: null }

    let query = client
      .from('expenses')
      .select('*, products(name)')
      .order('date', { ascending: false })

    if (options.productId) {
      query = query.eq('product_id', options.productId)
    }

    if (options.category) {
      query = query.eq('category', options.category)
    }

    if (options.startDate) {
      query = query.gte('date', options.startDate)
    }

    if (options.endDate) {
      query = query.lte('date', options.endDate)
    }

    const { data, error } = await query
    return { data: data || [], error }
  },

  async createExpense(expense) {
    const client = getSupabase()
    if (!client) return { data: null, error: 'Supabase not configured' }

    const { data, error } = await client
      .from('expenses')
      .insert([expense])
      .select()
      .single()

    return { data, error }
  },

  async getTeamMembers() {
    const client = getSupabase()
    if (!client) return { data: [], error: null }

    const { data, error } = await client
      .from('team_members')
      .select('*')
      .order('name')

    return { data: data || [], error }
  },

  async createTeamMember(member) {
    const client = getSupabase()
    if (!client) return { data: null, error: 'Supabase not configured' }

    const { data, error } = await client
      .from('team_members')
      .insert([member])
      .select()
      .single()

    return { data, error }
  },

  async updateTeamMember(id, updates) {
    const client = getSupabase()
    if (!client) return { data: null, error: 'Supabase not configured' }

    const { data, error } = await client
      .from('team_members')
      .update(updates)
      .eq('id', id)
      .select()
      .single()

    return { data, error }
  },

  async getProductAssignments() {
    const client = getSupabase()
    if (!client) return { data: [], error: null }

    const { data, error } = await client
      .from('product_assignments')
      .select('*, team_members(name, email), products(name)')
      .order('created_at', { ascending: false })

    return { data: data || [], error }
  },

  async createProductAssignment(assignment) {
    const client = getSupabase()
    if (!client) return { data: null, error: 'Supabase not configured' }

    const { data, error } = await client
      .from('product_assignments')
      .insert([assignment])
      .select('*, team_members(name, email), products(name)')
      .single()

    return { data, error }
  },

  async getMeetings() {
    const client = getSupabase()
    if (!client) return { data: [], error: null }

    const { data, error } = await client
      .from('meetings')
      .select('*, decisions(*), action_items(*)')
      .order('date', { ascending: false })

    return { data: data || [], error }
  },

  async createMeeting(meeting) {
    const client = getSupabase()
    if (!client) return { data: null, error: 'Supabase not configured' }

    const { data, error } = await client
      .from('meetings')
      .insert([meeting])
      .select()
      .single()

    return { data, error }
  },

  async createDecision(decision) {
    const client = getSupabase()
    if (!client) return { data: null, error: 'Supabase not configured' }

    const { data, error } = await client
      .from('decisions')
      .insert([decision])
      .select()
      .single()

    return { data, error }
  },

  async createActionItem(actionItem) {
    const client = getSupabase()
    if (!client) return { data: null, error: 'Supabase not configured' }

    const { data, error } = await client
      .from('action_items')
      .insert([actionItem])
      .select()
      .single()

    return { data, error }
  },

  async getDocuments() {
    const client = getSupabase()
    if (!client) return { data: [], error: null }

    const { data, error } = await client
      .from('documents')
      .select('*, products(name)')
      .order('created_at', { ascending: false })

    return { data: data || [], error }
  },

  async createDocument(document) {
    const client = getSupabase()
    if (!client) return { data: null, error: 'Supabase not configured' }

    const { data, error } = await client
      .from('documents')
      .insert([document])
      .select('*, products(name)')
      .single()

    return { data, error }
  },

  // ============================================
  // UNIFIED FINANCIALS (replaces separate transactions/expenses)
  // ============================================

  async getFinancials(options = {}) {
    const client = getSupabase()
    if (!client) return { data: [], error: null }

    let query = client
      .from('financials')
      .select('*, products(name)')
      .order('date', { ascending: false })

    if (options.productId) {
      query = query.eq('product_id', options.productId)
    }

    if (options.type) {
      query = query.eq('type', options.type)
    }

    if (options.category) {
      query = query.eq('category', options.category)
    }

    if (options.startDate) {
      query = query.gte('date', options.startDate)
    }

    if (options.endDate) {
      query = query.lte('date', options.endDate)
    }

    // Include studio-wide by default unless explicitly filtering by product
    if (options.includeStudioWide === false) {
      query = query.not('product_id', 'is', null)
    }

    const { data, error } = await query
    return { data: data || [], error }
  },

  async createFinancial(entry) {
    const client = getSupabase()
    if (!client) return { data: null, error: 'Supabase not configured' }

    const { data, error } = await client
      .from('financials')
      .insert([entry])
      .select('*, products(name)')
      .single()

    return { data, error }
  },

  async updateFinancial(id, updates) {
    const client = getSupabase()
    if (!client) return { data: null, error: 'Supabase not configured' }

    const { data, error } = await client
      .from('financials')
      .update(updates)
      .eq('id', id)
      .select('*, products(name)')
      .single()

    return { data, error }
  },

  async deleteFinancial(id) {
    const client = getSupabase()
    if (!client) return { error: 'Supabase not configured' }

    const { error } = await client
      .from('financials')
      .delete()
      .eq('id', id)

    return { error }
  },

  // ============================================
  // PAYMENTS
  // ============================================

  async getPayments(businessId = null, invoiceId = null) {
    const client = getSupabase()
    if (!client) return { data: [], error: null }

    let query = client
      .from('payments')
      .select('*, invoices(invoice_number, client_id, clients(name))')
      .order('created_at', { ascending: false })

    if (businessId) {
      query = query.eq('business_id', businessId)
    }

    if (invoiceId) {
      query = query.eq('invoice_id', invoiceId)
    }

    const { data, error } = await query
    return { data: data || [], error }
  },

  async createPayment(paymentData) {
    const client = getSupabase()
    if (!client) return { data: null, error: 'Supabase not configured' }

    const { data, error } = await client
      .from('payments')
      .insert([paymentData])
      .select()
      .single()

    return { data, error }
  },

  async updatePayment(id, updates) {
    const client = getSupabase()
    if (!client) return { data: null, error: 'Supabase not configured' }

    const { data, error } = await client
      .from('payments')
      .update(updates)
      .eq('id', id)
      .select()
      .single()

    return { data, error }
  },

  async deletePayment(id) {
    const client = getSupabase()
    if (!client) return { error: 'Supabase not configured' }

    const { error } = await client
      .from('payments')
      .delete()
      .eq('id', id)

    return { error }
  },
}

export default db
